import { localeMeta } from '@sohanscript/i18n/locale-meta';

/** Arabic-Indic digits ٠-٩ indexed by ASCII digit value. */
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const ARABIC_DECIMAL_SEPARATOR = '٫';
const ARABIC_GROUP_SEPARATOR = '٬';
const ARABIC_PERCENT_SIGN = '٪';

type DateInput = string | number | Date;

// NOTE: Bun's ICU resolves the bare "ar" locale to Latin digits
// (`numberingSystem: "latn"`), so Arabic-Indic output must be forced.
const arabicNumbering = (): Intl.NumberFormatOptions =>
	localeMeta().locale === 'ar' ? { numberingSystem: 'arab' } : {};

/** Locale-aware number formatting (ar → Arabic-Indic digits). */
export function formatNumber(
	value: number,
	options?: Intl.NumberFormatOptions,
): string {
	return new Intl.NumberFormat(localeMeta().locale, {
		...arabicNumbering(),
		...options,
	}).format(value);
}

/** Zero-padded index for badges (01 → ٠١). */
export function formatIndex(index: number): string {
	return formatNumber(index, { minimumIntegerDigits: 2, useGrouping: false });
}

/** Whole-number percent (95 → ٩٥٪). */
export function formatPercent(
	whole: number,
	options?: Intl.NumberFormatOptions,
): string {
	return new Intl.NumberFormat(localeMeta().locale, {
		...arabicNumbering(),
		style: 'percent',
		maximumFractionDigits: 0,
		...options,
	}).format(whole / 100);
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** Byte size with a binary unit (1536 → 1.5 KB). */
export function formatBytes(bytes: number | null | undefined): string | null {
	if (bytes == null) return null;
	if (bytes === 0) return '0 B';
	const exponent = Math.min(
		Math.floor(Math.log(bytes) / Math.log(1024)),
		BYTE_UNITS.length - 1,
	);
	return `${formatNumber(bytes / 1024 ** exponent, {
		minimumFractionDigits: exponent === 0 ? 0 : 1,
		maximumFractionDigits: exponent === 0 ? 0 : 1,
	})} ${BYTE_UNITS[exponent]}`;
}

function greatestCommonDivisor(left: number, right: number): number {
	let a = Math.abs(left);
	let b = Math.abs(right);
	while (b) {
		const next = a % b;
		a = b;
		b = next;
	}
	return a || 1;
}

/** Reduced aspect ratio plus its decimal form (1920×1080 → 16:9, 1.78). */
export function formatAspect(
	width: number | null | undefined,
	height: number | null | undefined,
): { label: string; decimal: string } | null {
	if (!width || !height) return null;
	const divisor = greatestCommonDivisor(width, height);
	return {
		label: `${formatNumber(width / divisor, { useGrouping: false })}:${formatNumber(height / divisor, { useGrouping: false })}`,
		decimal: formatNumber(width / height, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}),
	};
}

/** Locale-aware date-time formatting (medium date, short time). */
export function formatDateTime(
	value: DateInput,
	options?: Intl.DateTimeFormatOptions,
): string {
	return new Intl.DateTimeFormat(localeMeta().locale, {
		...arabicNumbering(),
		dateStyle: 'medium',
		timeStyle: 'short',
		...options,
	}).format(new Date(value));
}

/** Locale-aware date formatting with a stable UTC timezone. */
export function formatDate(
	value: DateInput,
	options?: Intl.DateTimeFormatOptions,
): string {
	return new Intl.DateTimeFormat(localeMeta().locale, {
		...arabicNumbering(),
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
		...options,
	}).format(new Date(value));
}

/**
 * Transliterates ASCII digits in already-formatted content strings
 * (٠-٩, plus numeric ٠٫٠ separators and ٩٥٪) for RTL locales.
 * No-op for non-RTL locales; safe to call on strings without numbers.
 */
export function localizeDigits(text: string): string {
	if (localeMeta().locale !== 'ar') return text;

	return text
		.replace(/[0-9]/g, (digit) => ARABIC_INDIC_DIGITS[Number(digit)])
		.replace(
			/([٠-٩])([,.]) ?(?=[٠-٩])/g,
			(_, digit: string, separator: string) =>
				digit +
				(separator === ',' ? ARABIC_GROUP_SEPARATOR : ARABIC_DECIMAL_SEPARATOR),
		)
		.replace(/([٠-٩]) ?%/g, (_, digit: string) => digit + ARABIC_PERCENT_SIGN);
}

/** Parse a comma-separated tags string into a clean array. */
export function parseTags(raw?: string | null): string[] {
	if (!raw) return [];
	return raw
		.split(',')
		.map((t) => t.trim())
		.filter(Boolean);
}
